import type { ProxyExtensionMeta } from "@/services/api/widget/types";
import type { DataPoint, ExtendedAssetInfo } from "./types";

export function getProxyExtensionsFromStats(
  assetStats?: Record<string, unknown>
): Record<string, ProxyExtensionMeta> {
  const metadata = assetStats?.metadata as
    | { proxy_extensions?: Record<string, ProxyExtensionMeta> }
    | undefined;
  return metadata?.proxy_extensions ?? {};
}

export function findProxyExtensionForAsset(
  assetId: string,
  assetMetadata: Record<string, ExtendedAssetInfo>,
  proxyExtensions: Record<string, ProxyExtensionMeta>
): ProxyExtensionMeta | undefined {
  const resourceType = assetMetadata[assetId]?.resourceType || "product";
  const typedKey = `${resourceType}:${assetId}`;
  return proxyExtensions[typedKey] ?? proxyExtensions[`product:${assetId}`] ?? proxyExtensions[assetId];
}

export function extractAssetValue(
  point: DataPoint,
  assetId: string
): number | null {
  let value = point[assetId];
  if (value === undefined || value === null) {
    const matchingKey = Object.keys(point).find((key) => key.endsWith(assetId));
    if (matchingKey) {
      value = point[matchingKey];
    }
  }
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }
  return value;
}

function toDateOnly(value: Date): number {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
}

function parseBoundary(value?: string | null): number | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : toDateOnly(parsed);
}

interface SegmentSeriesConfig {
  name: string;
  type: "line";
  data: Array<number | null>;
  smooth: boolean;
  showSymbol: boolean;
  connectNulls: boolean;
  showInLegend: boolean;
  lineStyle: { color: string; width: number; type?: "solid" | "dashed" };
  itemStyle: { color: string };
  emphasis: { focus: "series"; lineStyle: { width: number } };
  _resourceId: string;
  areaStyle?: { color: unknown };
}

export function buildProxyAwareLineSeries(params: {
  assetId: string;
  displayName: string;
  color: string;
  rawData: DataPoint[];
  extension?: ProxyExtensionMeta;
  smoothLines: boolean;
  showAreaGradient: boolean;
  createAreaGradient: (color: string, alpha: number) => unknown;
}): SegmentSeriesConfig[] {
  const {
    assetId,
    displayName,
    color,
    rawData,
    extension,
    smoothLines,
    showAreaGradient,
    createAreaGradient,
  } = params;

  const hasProxySegment =
    extension &&
    (extension.extended_at_start || extension.extended_at_end) &&
    (extension.real_data_starts || extension.real_data_ends);

  if (!hasProxySegment) {
    const data = rawData.map((point) => extractAssetValue(point, assetId));
    const series: SegmentSeriesConfig = {
      name: displayName,
      type: "line",
      data,
      smooth: smoothLines,
      showSymbol: false,
      connectNulls: true,
      showInLegend: true,
      lineStyle: { color, width: 2 },
      itemStyle: { color },
      emphasis: { focus: "series", lineStyle: { width: 3 } },
      _resourceId: assetId,
    };
    if (showAreaGradient) {
      series.areaStyle = { color: createAreaGradient(color, 0.3) };
    }
    return [series];
  }

  const realStart = parseBoundary(extension.real_data_starts);
  const realEnd = parseBoundary(extension.real_data_ends);

  const leftData: Array<number | null> = [];
  const realData: Array<number | null> = [];
  const rightData: Array<number | null> = [];

  rawData.forEach((point) => {
    const pointTime = toDateOnly(point.date instanceof Date ? point.date : new Date(point.date));
    const value = extractAssetValue(point, assetId);

    const inLeft =
      extension.extended_at_start &&
      realStart !== null &&
      pointTime < realStart;
    const inRight =
      extension.extended_at_end &&
      realEnd !== null &&
      pointTime > realEnd;
    const inReal =
      !inLeft &&
      !inRight &&
      (realStart === null || pointTime >= realStart) &&
      (realEnd === null || pointTime <= realEnd);

    leftData.push(inLeft ? value : null);
    realData.push(inReal ? value : null);
    rightData.push(inRight ? value : null);
  });

  const makeSeries = (
    data: Array<number | null>,
    dashed: boolean,
    showInLegend: boolean,
    withArea: boolean
  ): SegmentSeriesConfig => {
    const series: SegmentSeriesConfig = {
      name: displayName,
      type: "line",
      data,
      smooth: smoothLines,
      showSymbol: false,
      connectNulls: !dashed,
      showInLegend,
      lineStyle: {
        color,
        width: 2,
        ...(dashed ? { type: "dashed" as const } : {}),
      },
      itemStyle: { color },
      emphasis: { focus: "series", lineStyle: { width: 3 } },
      _resourceId: assetId,
    };
    if (withArea && showAreaGradient) {
      series.areaStyle = { color: createAreaGradient(color, 0.3) };
    }
    return series;
  };

  const segments: SegmentSeriesConfig[] = [];
  if (extension.extended_at_start) {
    segments.push(makeSeries(leftData, true, false, false));
  }
  segments.push(makeSeries(realData, false, true, true));
  if (extension.extended_at_end) {
    segments.push(makeSeries(rightData, true, false, false));
  }
  return segments;
}
